"use client";

import { useRouter } from "next/navigation";
import React, { useEffect, useRef } from "react";
import { ErrorSupportInfo } from "modules/common/components/ErrorSupportInfo";
import { captureError, getUserFriendlyMessage } from "utils/error-sanitizer";
import {
  claimChunkLoadRecovery,
  getChunkLoadTelemetry,
  isChunkLoadError,
} from "utils/chunk-load-recovery";
import { cleanErrorDocumentPathname } from "modules/common/analytics/pathSanitizer";

type ErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

function ErrorComponent({ error, reset }: ErrorProps) {
  const router = useRouter();
  const scheduledRecoveryKey = useRef<string | null>(null);
  const chunkLoadError = isChunkLoadError(error);

  useEffect(() => {
    const chunkTelemetry = getChunkLoadTelemetry(error, {
      documentPathname: cleanErrorDocumentPathname(window.location.pathname),
    });
    const recovery = claimChunkLoadRecovery(chunkTelemetry);

    if (
      recovery.shouldReload &&
      recovery.recoveryKey &&
      scheduledRecoveryKey.current !== recovery.recoveryKey
    ) {
      scheduledRecoveryKey.current = recovery.recoveryKey;
      // Schedule recovery before diagnostics so an analytics SDK failure can
      // never strand the user on an unloadable document.
      window.setTimeout(() => window.location.reload(), 250);
    }

    try {
      captureError(error, {
        error_boundary: "root",
        digest: error.digest,
        ...chunkTelemetry,
        chunk_reload_recovery: recovery.status,
      });
    } catch {
      // Error reporting is best-effort; the recovery timer above is primary.
    }
  }, [error]);

  return (
    <div className="flex min-h-[calc(100vh-64px)] flex-col items-center justify-center p-4 text-center">
      <div className="card bg-base-200 w-full max-w-md shadow-lg">
        <div className="card-body">
          <h2 className="card-title text-error mb-2 justify-center text-xl md:text-2xl">
            Something went wrong!
          </h2>

          <div className="py-3">
            <p className="text-base-content">{getUserFriendlyMessage(error)}</p>

            {error.digest && (
              <div className="badge badge-neutral mx-auto mt-3">
                ID: {error.digest}
              </div>
            )}
          </div>

          <div className="card-actions mt-2 justify-center">
            <button
              onClick={() => {
                if (chunkLoadError) {
                  window.location.reload();
                  return;
                }
                router.refresh();
                reset();
              }}
              className="btn btn-primary"
            >
              Try Again
            </button>
          </div>
          <ErrorSupportInfo className="mt-4" />
        </div>
      </div>
    </div>
  );
}

export default ErrorComponent;
